/**
 * Safe Bugsnag wrapper that handles lazy loading
 * All Bugsnag calls will queue until Bugsnag is initialized
 */

let bugsnagInstance: any = null;
const callQueue: Array<() => void> = [];

// Initialize Bugsnag lazily
const initBugsnag = async () => {
  if (bugsnagInstance) return bugsnagInstance;

  // Only initialize after page is interactive
  if (document.readyState === "loading") {
    await new Promise((resolve) => {
      if (document.readyState === "complete") {
        resolve(undefined);
      } else {
        document.addEventListener("DOMContentLoaded", resolve, { once: true });
      }
    });
  }

  // Use requestIdleCallback if available, otherwise setTimeout
  await new Promise<void>((resolve) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => resolve(), { timeout: 3000 });
    } else {
      setTimeout(() => resolve(), 100);
    }
  });

  const [{ default: Bugsnag }, { default: BugsnagPluginReact }] =
    await Promise.all([import("@bugsnag/js"), import("@bugsnag/plugin-react")]);

  const { BUGSNAG_API_KEY } = await import("../configs");
  const { exceptions } = await import("../../features/error-boundary/model");

  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    maxBreadcrumbs: 5,
    enabledBreadcrumbTypes: ["manual"],
    onError: ((event: any) => {
      const isStringError = typeof event.originalError === "string";
      const doNotTrack = exceptions.some((exception: string) =>
        event.originalError.includes(exception),
      );

      if (isStringError && doNotTrack) {
        return false;
      }
    }) as any,
  });

  bugsnagInstance = Bugsnag;

  // Mark as initialized globally (for fetcher to check without importing)
  if (typeof window !== "undefined") {
    (window as any).__bugsnagInitialized = true;
  }

  // Process queued calls
  callQueue.forEach((fn) => fn());
  callQueue.length = 0;

  return bugsnagInstance;
};

// Don't initialize immediately - only initialize when actually needed (on first error)
// This prevents Bugsnag from loading during the critical path

export const BugsnagSafe = {
  notify: (error: Error, callback?: (event: any) => void) => {
    if (bugsnagInstance) {
      return bugsnagInstance.notify(error, callback);
    } else {
      // Queue the call and initialize only when first error occurs
      callQueue.push(() => {
        if (bugsnagInstance) {
          bugsnagInstance.notify(error, callback);
        }
      });
      // Initialize in background, but don't block
      initBugsnag().catch(() => {});
    }
  },
  leaveBreadcrumb: (message: string, metadata?: Record<string, any>) => {
    if (bugsnagInstance) {
      return bugsnagInstance.leaveBreadcrumb(message, metadata);
    } else {
      // Breadcrumbs are non-critical - just queue them, don't initialize Bugsnag
      // Bugsnag will only be initialized when notify() is called (actual errors)
      callQueue.push(() => {
        if (bugsnagInstance) {
          bugsnagInstance.leaveBreadcrumb(message, metadata);
        }
      });
      // Don't initialize for breadcrumbs - wait for actual errors
    }
  },
};
